import React from 'react';
import { Link } from 'react-router-dom';
import { formatPrice } from '../../lib/shopify';
import { useCart } from '../../context/CartContext';
import { ShoppingBag, Droplet, Shield } from 'lucide-react';

const ProductCard = ({ product }) => {
  const { addItem, loading } = useCart();
  const mainImage = product.images?.[0];
  const secondImage = product.images?.[1];
  const defaultVariant = product.variants?.[0];

  const handleQuickAdd = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (defaultVariant?.id) {
      const productInfo = {
        title: product.title,
        variantTitle: defaultVariant.title,
        price: defaultVariant.price?.amount || product.price,
        image: mainImage?.url,
        handle: product.handle
      };
      addItem(defaultVariant.id, 1, productInfo);
    }
  };

  return (
    <Link
      to={`/product/${product.handle}`}
      data-testid={`product-card-${product.handle}`}
      className="product-card group block"
    >
      {/* Image Container */}
      <div className="relative aspect-square bg-[#111] overflow-hidden mb-4">
        {/* Main Image */}
        {mainImage ? (
          <img
            src={mainImage.url}
            alt={mainImage.altText || product.title}
            className="product-image absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            width={mainImage.width || 400}
            height={mainImage.height || 400}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/20">
            <ShoppingBag className="w-16 h-16" />
          </div>
        )}

        {/* Second Image on Hover */}
        {secondImage && (
          <img
            src={secondImage.url}
            alt={secondImage.altText || `${product.title} alternate view`}
            className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            loading="lazy"
          />
        )}

        {/* Overlay */}
        <div className="product-overlay" />

        {/* Badges */}
        <div className="absolute top-3 left-3 z-10 flex flex-col space-y-2">
          <span className="badge-waterproof flex items-center space-x-1">
            <Droplet className="w-3 h-3" />
            <span>Waterproof</span>
          </span>
          <span className="badge-tarnish-free flex items-center space-x-1">
            <Shield className="w-3 h-3" />
            <span>Tarnish-Free</span>
          </span>
        </div>

        {/* Quick Add Button */}
        <button
          onClick={handleQuickAdd}
          disabled={loading || !defaultVariant?.availableForSale}
          data-testid={`quick-add-${product.handle}`}
          className={`absolute bottom-4 left-4 right-4 py-3 bg-black/80 backdrop-blur-sm border border-[#D4AF37] text-[#D4AF37] 
            font-accent text-xs uppercase tracking-[0.2em] 
            translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 
            hover:bg-[#D4AF37] hover:text-black transition-all duration-300
            disabled:opacity-50 disabled:cursor-not-allowed z-20`}
        >
          {loading ? 'Adding...' : defaultVariant?.availableForSale ? 'Quick Add' : 'Sold Out'}
        </button>
      </div>

      {/* Product Info */}
      <div className="space-y-2">
        <h3 className="text-white text-sm font-medium group-hover:text-[#D4AF37] transition-colors line-clamp-2">
          {product.title}
        </h3>
        <p className="text-[#D4AF37] font-medium">
          {formatPrice(product.price, product.currency)}
        </p>
      </div>
    </Link>
  );
};

export default ProductCard;
